import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Link2, PlusCircle, X } from "react-feather";
import toast from "react-hot-toast";
import StatusBadge from "../../../components/common/StatusBadge";
import CollapsibleCard, { PANEL_ACCENT } from "./CollapsibleCard";
import { emptyRow } from "../../stock/components/LineItemsEditor";
import QuotationCreateModal from "../quotations/components/QuotationCreateModal";
import { useListQuotationsQuery, useSetQuotationSalesLinkMutation } from "../quotations/quotationsApi";
import { useCanViewQuotations, useCanEditQuotation, useCanCreateQuotation } from "../quotations/useQuotationPermissions";
import { QUOTATION_STATUS_CLASS, QUOTATION_STATUS_LABEL } from "../quotations/quotationStatusMaps";
import { paths } from "../../../routes/paths";
import { DEFAULT_VAT_PERCENT } from "../../../components/common/vatDefaults";

// Quotation cross-link from the sales side (Batch164). A quotation already
// points at an enquiry (SalesEnquiryRecordGuid); this panel lets the enquiry /
// order screen list those, link another of the same customer's quotations, or
// raise a new one pre-filled from the priced service lines.

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const LINKABLE = ["SUBMITTED", "APPROVED"];

/** Quotation line rows from the priced service lines, as FREETEXT lines. */
const rowsFromServiceLines = (serviceLines, vatPercent) =>
  (serviceLines || []).map((l, i) => ({
    ...emptyRow(),
    key: `svc-${l.serviceTypeId}-${Date.now()}-${i}`,
    lineType: "FREETEXT",
    productCode: "",
    productName: l.serviceTypeName,
    qty: String(Number(l.qty) > 0 ? Number(l.qty) : 1),
    rate: l.rate != null ? String(Number(l.rate)) : "0",
    vatPercent: String(vatPercent),
  }));

/**
 * enquiryRecordGuid - the enquiry the quotations hang off (an order passes its originating enquiry).
 * reference         - { key, salesEnquiryRecordGuid, salesReferenceNumber } for the create modal's picker.
 */
const SalesQuotationsPanel = ({
  enquiryRecordGuid, branchId, custId, customerBranchId, customerName, reference, serviceLines, vatRate,
}) => {
  const canView = useCanViewQuotations();
  const canEdit = useCanEditQuotation();
  const canCreate = useCanCreateQuotation();
  const [showPicker, setShowPicker] = useState(false);
  const [pickedGuid, setPickedGuid] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [setSalesLink, { isLoading: isLinking }] = useSetQuotationSalesLinkMutation();

  const { data: linkedPage } = useListQuotationsQuery(
    { salesEnquiryRecordGuid: enquiryRecordGuid, pageSize: 20 },
    { skip: !canView || !enquiryRecordGuid }
  );
  const linked = linkedPage?.items || [];

  const { data: customerPage, isFetching: isFetchingCustomer } = useListQuotationsQuery(
    { custId, pageSize: 50 },
    { skip: !showPicker || !custId }
  );
  const candidates = useMemo(
    () => (customerPage?.items || []).filter((q) => LINKABLE.includes(q.status) && q.salesEnquiryRecordGuid !== enquiryRecordGuid),
    [customerPage, enquiryRecordGuid]
  );

  const prefill = useMemo(() => {
    const vat = Number(vatRate) > 0 ? Number(vatRate) : DEFAULT_VAT_PERCENT;
    return {
      branchId,
      customerOption: custId
        ? { value: `${customerBranchId}-${custId}`, label: customerName, customer: { custId, branchId: customerBranchId } }
        : null,
      salesReference: reference,
      rows: rowsFromServiceLines(serviceLines, vat),
    };
  }, [branchId, custId, customerBranchId, customerName, reference, serviceLines, vatRate]);

  if (!canView) return null;

  const link = async () => {
    if (!pickedGuid) return toast.error("Pick a quotation to link");
    try {
      await setSalesLink({ recordGuid: pickedGuid, salesEnquiryRecordGuid: enquiryRecordGuid }).unwrap();
      toast.success("Quotation linked");
      setPickedGuid("");
      setShowPicker(false);
    } catch (e) {
      toast.error(e?.message || "Could not link the quotation");
    }
  };

  const unlink = async (q) => {
    if (!window.confirm(`Unlink quotation ${q.quotationNumber} from this ${reference?.key?.startsWith("order") ? "order" : "enquiry"}?`)) return;
    try {
      await setSalesLink({ recordGuid: q.recordGuid, salesEnquiryRecordGuid: null }).unwrap();
      toast.success("Quotation unlinked");
    } catch (e) {
      toast.error(e?.message || "Could not unlink the quotation");
    }
  };

  return (
    <>
      <CollapsibleCard
        title="Quotations"
        icon={FileText}
        accent={PANEL_ACCENT.document}
        hasData={linked.length > 0}
        count={linked.length}
        forceOpen={showPicker}
        summary={linked.length > 0 ? `latest ${linked[0].quotationNumber}` : null}
        actions={
          <>
            {canEdit && custId && (
              <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" onClick={() => setShowPicker((s) => !s)}>
                <Link2 size={14} /> Link existing
              </button>
            )}
            {canCreate && (
              <button type="button" className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1" onClick={() => setShowCreate(true)}>
                <PlusCircle size={14} /> Create from lines
              </button>
            )}
          </>
        }
      >
        {showPicker && (
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3 p-2 rounded" style={{ background: "#F8F9FA", border: "1px solid #E9ECEF" }}>
            <select className="form-control form-control-sm" style={{ maxWidth: 420 }} value={pickedGuid} onChange={(e) => setPickedGuid(e.target.value)} disabled={isFetchingCustomer}>
              <option value="">
                {isFetchingCustomer ? "Loading..." : candidates.length ? "Select one of this customer's quotations" : "No other open quotations for this customer"}
              </option>
              {candidates.map((q) => (
                <option key={q.recordGuid} value={q.recordGuid}>
                  {q.quotationNumber} · {new Date(q.quotationDate).toLocaleDateString()} · {money(q.totalAmount)} · {QUOTATION_STATUS_LABEL[q.status] || q.status}
                  {q.salesReferenceNumber ? ` · now on ${q.salesReferenceNumber}` : ""}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-primary btn-sm" onClick={link} disabled={isLinking || !pickedGuid}>Link</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowPicker(false)}>Cancel</button>
          </div>
        )}
        {linked.length === 0 ? (
          <div className="text-muted small">No quotation is linked yet.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr><th>Quotation</th><th>Date</th><th>Status</th><th className="text-end">Total</th>{canEdit && <th style={{ width: 40 }} />}</tr>
              </thead>
              <tbody>
                {linked.map((q) => (
                  <tr key={q.recordGuid}>
                    <td><Link to={paths.quotationDetail(q.recordGuid)} className="fw-semibold">{q.quotationNumber}</Link></td>
                    <td>{new Date(q.quotationDate).toLocaleDateString()}</td>
                    <td><StatusBadge status={q.status} classMap={QUOTATION_STATUS_CLASS} labelMap={QUOTATION_STATUS_LABEL} /></td>
                    <td className="text-end fw-semibold">{money(q.totalAmount)}</td>
                    {canEdit && (
                      <td className="text-end">
                        <button type="button" className="btn btn-link btn-sm text-danger p-0" title="Unlink" onClick={() => unlink(q)} disabled={isLinking}>
                          <X size={15} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CollapsibleCard>

      <QuotationCreateModal show={showCreate} onClose={() => setShowCreate(false)} prefill={prefill} stayOnPage />
    </>
  );
};

export default SalesQuotationsPanel;
